import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

/* Two galleries, one page.
   Roofing photos live in public/projects, coating in public/coating-projects.
   Each folder carries its own projects.json, so adding a job never needs a code change. */
const SOURCES = [
  { service: 'Roofing', base: '/projects', manifest: '/projects/projects.json', img: '/projects/img' },
  { service: 'Coating', base: '/coating-projects', manifest: '/coating-projects/projects.json' },
];

/* Folder names contain spaces, commas and ampersands. Encode each segment so
   every path is a valid URL without mangling the slashes. */
const enc = (p) => String(p).split('/').map(encodeURIComponent).join('/');
/* Matches the badge colour on the cards, so a filter and a card read as the same thing. */
const SERVICE_COLOUR = { Roofing: '#C2703A', Coating: '#1a3f7b', 'Windows & Doors': '#3E6B8A' };
const isVideo = (f) => /\.(mp4|mov|m4v|webm)$/i.test(f);

/* Show a homeowner what someone else paid and they anchor on the number
   without knowing the size of the job. Flip to true if that changes. */
const SHOW_PRICE = false;

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,600;9..144,700&family=Montserrat:wght@500;600;700;800&display=swap');
.pj{--ink:#0f1724;--dim:#686e77;--line:#e2e5e9;--gold:#C9922A;--green:#0d1f3b;--paper:#fff;
  font-family:Montserrat,system-ui,sans-serif;color:var(--ink);padding-bottom:56px}
.pj-eb{font-size:10.5px;font-weight:800;letter-spacing:.2em;text-transform:uppercase;color:var(--gold);margin-bottom:7px}
.pj-h{font-family:Fraunces,Georgia,serif;font-size:clamp(25px,3.3vw,33px);font-weight:700;margin:0;letter-spacing:-.02em;line-height:1.06}
.pj-l{color:var(--dim);font-size:13.5px;margin-top:8px}
.pj-back{background:none;border:none;font:inherit;font-size:13px;font-weight:700;color:var(--dim);cursor:pointer;
  padding:0;margin-bottom:14px;display:inline-flex;gap:7px;align-items:center}
.pj-back:hover{color:var(--ink)}
.pj-chips{display:flex;gap:10px;flex-wrap:wrap;margin:24px 0 0}
.pj-chip{display:inline-flex;align-items:center;gap:10px;font:inherit;font-size:14.5px;font-weight:700;
  padding:13px 22px;border-radius:999px;border:1.5px solid var(--line);background:var(--paper);color:var(--ink);
  cursor:pointer;box-shadow:0 1px 2px rgba(15,36,29,.05);
  transition:border-color .2s,background .2s,transform .2s cubic-bezier(.2,.8,.3,1),box-shadow .2s}
.pj-chip:hover{border-color:#c0c6cf;transform:translateY(-2px);box-shadow:0 8px 18px -10px rgba(15,36,29,.4)}
.pj-chip:active{transform:translateY(0)}
.pj-chip:focus-visible{outline:3px solid var(--gold);outline-offset:3px}
/* a dot in the service colour, so the filters match the badges on the cards */
.pj-dot{width:9px;height:9px;border-radius:50%;background:var(--c);flex:0 0 auto}
.pj-chip .n{background:#eef0f3;color:#5a616b;font-size:12px;font-weight:800;
  padding:3px 9px;border-radius:999px;min-width:26px;text-align:center}
.pj-chip.on{background:var(--green);border-color:var(--green);color:#fff;
  box-shadow:0 6px 16px -8px rgba(13,59,46,.7)}
.pj-chip.on .n{background:rgba(255,255,255,.2);color:#fff}
.pj-chip.on .pj-dot{background:#fff}
@media(max-width:600px){.pj-chips{gap:8px}.pj-chip{padding:12px 17px;font-size:13.5px}}
.pj-count{font-size:12.5px;color:var(--dim);font-weight:600;margin:18px 0 0}
.pj-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(290px,1fr));gap:22px;margin-top:20px}
.pj-card{display:flex;flex-direction:column;border:1px solid var(--line);padding:0;font:inherit;text-align:left;
  cursor:pointer;border-radius:18px;overflow:hidden;background:var(--paper);
  box-shadow:0 1px 2px rgba(15,36,29,.05);
  transition:transform .28s cubic-bezier(.2,.8,.3,1),box-shadow .28s,border-color .28s}
.pj-card:hover{transform:translateY(-4px);border-color:#cfd4db;box-shadow:0 20px 38px -22px rgba(15,36,29,.45)}
.pj-card:focus-visible{outline:3px solid var(--gold);outline-offset:3px}
/* the photograph, undimmed — this is the work being sold */
.pj-thumb{position:relative;aspect-ratio:4/3;overflow:hidden;background:#e4e6ea;flex:0 0 auto}
.pj-card img{width:100%;height:100%;object-fit:cover;display:block;
  transition:transform .55s cubic-bezier(.2,.8,.3,1)}
.pj-card:hover img{transform:scale(1.045)}
.pj-hasvid{position:absolute;top:11px;right:12px;display:inline-flex;align-items:center;gap:6px;
  background:rgba(8,22,17,.74);color:#fff;font-size:11px;font-weight:800;padding:4px 11px;border-radius:999px;
  backdrop-filter:blur(5px)}
.pj-hasvid span{font-size:9px}
.pj-svc{position:absolute;top:11px;left:12px;background:rgba(8,22,17,.74);color:#fff;font-size:9.5px;font-weight:800;
  letter-spacing:.08em;padding:4px 10px;border-radius:999px;backdrop-filter:blur(5px)}
/* the words, on white, where they can always be read */
.pj-cb{padding:16px 17px 17px;display:flex;flex-direction:column;flex:1 1 auto}
.pj-cb h2{font-family:Fraunces,Georgia,serif;font-size:18.5px;margin:0;font-weight:700;letter-spacing:-.013em;
  line-height:1.24;color:var(--ink)}
.pj-cb p{margin:6px 0 12px;font-size:13px;color:var(--dim);line-height:1.5}
.pj-meta{margin-top:auto;padding-top:12px;border-top:1px solid #eff1f3;display:flex;gap:7px;flex-wrap:wrap;align-items:center}
.pj-pill{background:#eef0f3;color:#484f59;border-radius:999px;padding:5px 12px;font-size:11.5px;font-weight:700}
.pj-pill.count{background:none;color:#8b9097;padding:5px 0;margin-left:auto}
.pj-feat{margin:22px 0 8px;background:linear-gradient(135deg,#132b51,#0d1f3b);border-radius:20px;
  padding:20px 22px;color:#fff}
.pj-feat h3{font-family:Fraunces,Georgia,serif;font-size:19px;margin:0 0 4px;font-weight:700;letter-spacing:-.012em}
.pj-feat p{margin:0 0 15px;font-size:12.5px;opacity:.82}
.pj-featbtn{position:relative;display:block;width:100%;padding:0;border:none;border-radius:14px;overflow:hidden;
  background:#000;aspect-ratio:16/9;cursor:pointer;box-shadow:0 12px 30px -14px rgba(0,0,0,.8)}
.pj-featbtn img{width:100%;height:100%;object-fit:cover;display:block;transition:transform .5s cubic-bezier(.2,.8,.3,1)}
.pj-featbtn:hover img{transform:scale(1.035)}
.pj-featbtn:focus-visible{outline:3px solid var(--gold);outline-offset:3px}
.pj-featplay{position:absolute;inset:0;display:grid;place-items:center;background:rgba(8,22,17,.24);transition:background .24s}
.pj-featbtn:hover .pj-featplay{background:rgba(8,22,17,.1)}
.pj-featplay span{width:66px;height:66px;border-radius:50%;background:rgba(255,255,255,.96);color:var(--green);
  display:grid;place-items:center;font-size:23px;padding-left:4px;box-shadow:0 6px 20px rgba(0,0,0,.45);
  transition:transform .24s cubic-bezier(.3,1.4,.5,1)}
.pj-featbtn:hover .pj-featplay span{transform:scale(1.1)}
.pj-featlen{position:absolute;bottom:11px;right:12px;background:rgba(8,22,17,.85);color:#fff;font-size:11.5px;
  font-weight:700;padding:4px 10px;border-radius:6px}
.pj-stage{font-family:Fraunces,Georgia,serif;font-size:18px;font-weight:700;margin:26px 0 3px}
.pj-ssub{font-size:12.5px;color:var(--dim);margin-bottom:12px}
.pj-shots{display:grid;grid-template-columns:repeat(auto-fill,minmax(180px,1fr));gap:10px}
.pj-shot{position:relative;border:none;padding:0;background:#dde0e5;border-radius:13px;overflow:hidden;
  aspect-ratio:4/3;cursor:pointer;transition:transform .22s cubic-bezier(.2,.8,.3,1),box-shadow .22s}
.pj-shot img,.pj-shot video{width:100%;height:100%;object-fit:cover;display:block}
.pj-shot:hover{transform:scale(1.03);box-shadow:0 12px 24px -14px rgba(15,36,29,.5)}
.pj-shot:focus-visible{outline:3px solid var(--gold);outline-offset:2px}
.pj-vbadge{position:absolute;inset:0;display:grid;place-items:center;background:rgba(10,26,20,.28);pointer-events:none}
.pj-vbadge span{width:42px;height:42px;border-radius:50%;background:rgba(255,255,255,.94);color:var(--green);
  display:grid;place-items:center;font-size:15px;padding-left:2px;box-shadow:0 3px 12px rgba(0,0,0,.35)}
.pj-lb{position:fixed;inset:0;background:rgba(6,15,12,.95);z-index:9999;display:flex;align-items:center;justify-content:center}
.pj-lb img,.pj-lb video{max-width:94vw;max-height:88vh;border-radius:8px;background:#000}
.pj-lbx,.pj-lbn,.pj-lbp{position:absolute;background:rgba(255,255,255,.14);border:none;color:#fff;cursor:pointer;
  border-radius:50%;width:46px;height:46px;font-size:19px}
.pj-lbx{top:18px;right:20px}.pj-lbp{left:18px;top:50%;transform:translateY(-50%)}
.pj-lbn{right:18px;top:50%;transform:translateY(-50%)}
.pj-lbx:hover,.pj-lbn:hover,.pj-lbp:hover{background:rgba(255,255,255,.3)}
.pj-lbc{position:absolute;bottom:20px;left:50%;transform:translateX(-50%);color:rgba(255,255,255,.75);font-size:12.5px;font-weight:600}
.pj-empty{border:1px dashed #d5d8de;border-radius:16px;padding:34px;text-align:center;color:var(--dim);font-size:13.5px}
@media(max-width:560px){.pj-shots{grid-template-columns:1fr 1fr;gap:8px}.pj-lbp,.pj-lbn{width:40px;height:40px}}
`;

export default function CompletedProjects() {
  const [params, setParams] = useSearchParams();
  const slug = params.get('job');
  const svc = params.get('service') || 'All';
  const [projects, setProjects] = useState(null);
  const [lb, setLb] = useState(null);

  useEffect(() => {
    Promise.all(SOURCES.map(src =>
      fetch(src.manifest)
        .then(r => (r.ok ? r.json() : []))
        .then(list => (Array.isArray(list) ? list : []).map(p => normalise(p, src)))
        .catch(() => [])
    )).then(all => setProjects([].concat(...all)));
  }, []);

  /* Both manifests describe the same thing in slightly different shapes.
     Flatten them here so the rest of the page only deals with one. */
  function normalise(p, src) {
    const stages = (p.stages || []).map(st => {
      const files = st.files || st.photos || [];
      return {
        name: st.name,
        items: files.map(f => ({
          src: src.img ? `${src.img}/${enc(f)}` : `${src.base}/${enc(p.base)}/${enc(f)}`,
          video: isVideo(f),
        })),
      };
    }).filter(st => st.items.length);
    const cover = p.cover
      ? (src.img ? `${src.img}/${enc(p.cover)}` : `${src.base}/${enc(p.base)}/${enc(p.cover)}`)
      : (stages.length ? stages[stages.length - 1].items[0].src : null);
    // Coating jobs come from a folder scan and carry no description, so
    // describe them by what is actually in the album.
    const names = stages.map(x => x.name.toLowerCase());
    const hasBA = names.includes('before') && names.includes('after');
    const vids = stages.reduce((a, x) => a + x.items.filter(i => i.video).length, 0);
    const auto = hasBA
      ? 'Before, during and after the work' + (vids ? ', with video' : '')
      : (vids ? 'Photos and video from the job' : 'Photos from the job');

    const photoCount = stages.reduce((a, x) => a + x.items.filter(i => !i.video).length, 0);

    // A before/after video, shown prominently above the photo stages so it
    // does not get lost among a hundred stills.
    const feat = p.featured_video
      ? {
          src: `${src.base}/${enc(p.base)}/${enc(p.featured_video)}`,
          poster: `${src.base}/${enc(p.base)}/${enc(p.featured_video.replace(/\.[^.]+$/, '.jpg'))}`,
          label: p.featured_label || 'Before & after',
          length: p.featured_length || '',
        }
      : null;

    return {
      featured: feat,
      videos: vids, photos: photoCount,
      slug: p.slug, title: p.title, blurb: p.blurb || auto, price: p.price || '',
      pitch: p.pitch || '', service: p.service || src.service,
      count: p.count || stages.reduce((a, s) => a + s.items.length, 0),
      stages, cover,
    };
  }

  const setParam = (k, v) => {
    const n = new URLSearchParams(params);
    v ? n.set(k, v) : n.delete(k);
    setParams(n); window.scrollTo(0, 0);
  };
  const project = projects?.find(p => p.slug === slug) || null;
  const services = projects ? [...new Set(projects.map(p => p.service))] : [];
  const shown = projects ? projects.filter(p => svc === 'All' || p.service === svc) : [];

  useEffect(() => {
    if (!lb) return;
    const key = (e) => {
      if (e.key === 'Escape') setLb(null);
      if (e.key === 'ArrowRight') setLb(v => ({ ...v, index: (v.index + 1) % v.items.length }));
      if (e.key === 'ArrowLeft') setLb(v => ({ ...v, index: (v.index - 1 + v.items.length) % v.items.length }));
    };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, [lb]);

  const shot = lb && lb.items[lb.index];

  return (
    <>
      <style>{CSS}</style>
      <div className="page-header"><h2>📸 See Our Work</h2></div>

      <div className="page-body pj">
        {lb && shot && (
          <div className="pj-lb" onClick={e => { if (e.target === e.currentTarget) setLb(null); }}>
            {shot.video
              ? <video src={shot.src} controls autoPlay playsInline />
              : <img src={shot.src} alt={`${lb.stage} ${lb.index + 1}`} />}
            <button className="pj-lbx" onClick={() => setLb(null)} aria-label="Close">✕</button>
            <button className="pj-lbp" aria-label="Previous"
              onClick={() => setLb(v => ({ ...v, index: (v.index - 1 + v.items.length) % v.items.length }))}>‹</button>
            <button className="pj-lbn" aria-label="Next"
              onClick={() => setLb(v => ({ ...v, index: (v.index + 1) % v.items.length }))}>›</button>
            <div className="pj-lbc">{lb.stage} · {lb.index + 1} of {lb.items.length}</div>
          </div>
        )}

        {!projects ? <div style={{ padding: 26, color: '#aaa' }}>Loading…</div>
          : !project ? (<>
            <div className="pj-eb">Amanda's Home Improvement</div>
            <h1 className="pj-h">See our work</h1>
            <div className="pj-l">Real jobs, start to finish. Show a homeowner what the work actually looks like.</div>

            {services.length > 1 && (
              <div className="pj-chips">
                {['All', ...services].map(s => (
                  <button key={s} className={'pj-chip' + (svc === s ? ' on' : '')}
                    style={{ '--c': SERVICE_COLOUR[s] || '#787d84' }}
                    onClick={() => setParam('service', s === 'All' ? '' : s)}>
                    {s !== 'All' && <span className="pj-dot" aria-hidden />}
                    {s === 'All' ? 'All work' : s}
                    <span className="n">{s === 'All' ? projects.length : projects.filter(p => p.service === s).length}</span>
                  </button>
                ))}
              </div>
            )}
            <div className="pj-count">
              {svc === 'All'
                ? (() => {
                    const ph = shown.reduce((a, p) => a + p.photos, 0);
                    const v = shown.reduce((a, p) => a + p.videos, 0);
                    return `${shown.length} jobs · ${ph.toLocaleString()} photos${v ? ` · ${v} videos` : ''}`;
                  })()
                : `${shown.length} ${svc.toLowerCase()} job${shown.length === 1 ? '' : 's'}`}
            </div>

            {shown.length === 0 ? <div className="pj-empty" style={{ marginTop: 18 }}>Nothing here yet.</div> : (
              <div className="pj-grid">
                {shown.map(p => (
                  <button key={p.service + p.slug} className="pj-card" onClick={() => setParam('job', p.slug)}>
                    <span className="pj-thumb">
                      {p.cover && <img src={p.cover} alt={p.title} loading="lazy" />}
                      <span className="pj-svc">{p.service}</span>
                      {p.videos > 0 && (
                        <span className="pj-hasvid" title={`${p.videos} video${p.videos === 1 ? '' : 's'}`}>
                          <span aria-hidden>▶</span>{p.videos}
                        </span>
                      )}
                    </span>
                    <span className="pj-cb">
                      <h2>{p.title}</h2>
                      {p.blurb && <p>{p.blurb}</p>}
                      <span className="pj-meta">
                        {p.pitch && <span className="pj-pill">{p.pitch}</span>}
                        {SHOW_PRICE && p.price && <span className="pj-pill">{p.price}</span>}
                        <span className="pj-pill count">
                          {p.photos} photo{p.photos === 1 ? '' : 's'}
                          {p.videos > 0 && <> · {p.videos} video{p.videos === 1 ? '' : 's'}</>}
                        </span>
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </>) : (<>
            <button className="pj-back" onClick={() => setParam('job', '')}>← All projects</button>
            <div className="pj-eb">{project.service} · completed project</div>
            <h1 className="pj-h">{project.title}</h1>
            <div className="pj-l">
              {project.blurb}{project.pitch ? ` · ${project.pitch}` : ''}
              {SHOW_PRICE && project.price ? ` · ${project.price}` : ''}
            </div>
            {project.featured && (
              <div className="pj-feat">
                <h3>Watch the before &amp; after</h3>
                <p>The whole job in under a minute — the fastest way to show a homeowner what changes.</p>
                <button className="pj-featbtn"
                  onClick={() => setLb({ items: [{ src: project.featured.src, video: true }], index: 0, stage: project.featured.label })}>
                  <img src={project.featured.poster} alt={project.featured.label} loading="lazy"
                    onError={e => { e.currentTarget.style.display = 'none'; }} />
                  <span className="pj-featplay"><span aria-hidden>▶</span></span>
                  {project.featured.length && <span className="pj-featlen">{project.featured.length}</span>}
                </button>
              </div>
            )}

            {project.stages.map(st => (
              <div key={st.name}>
                <div className="pj-stage">{st.name}</div>
                <div className="pj-ssub">
                  {(() => {
                    const v = st.items.filter(x => x.video).length;
                    const ph = st.items.length - v;
                    return [
                      ph ? `${ph} photo${ph === 1 ? '' : 's'}` : null,
                      v ? `${v} video${v === 1 ? '' : 's'}` : null,
                    ].filter(Boolean).join(' · ');
                  })()}
                </div>
                <div className="pj-shots">
                  {st.items.map((it, i) => (
                    <button key={it.src} className="pj-shot"
                      onClick={() => setLb({ items: st.items, index: i, stage: st.name })}>
                      {it.video
                        ? <><video src={it.src + '#t=1'} preload="metadata" muted playsInline />
                            <span className="pj-vbadge"><span aria-hidden>▶</span></span></>
                        : <img src={it.src} alt={`${project.title} — ${st.name} ${i + 1}`} loading="lazy" />}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </>)}
      </div>
    </>
  );
}
